import React, { useLayoutEffect, useMemo } from "react";
import { Stack } from "react-bootstrap";
import { PageHeader } from "../../../../CommonElements/PageTitle";
import { useTranslation } from "react-i18next";
import { useSearchParams, useParams } from "react-router-dom";
import { Tabs } from "antd";
import { HugeiconsIcon } from "@hugeicons/react";
import { City03Icon, Mosque02Icon } from "@hugeicons/core-free-icons/index";
import { Container } from "reactstrap";
import { FormPortsTab } from "./ReceptionCenter/FormPortsTab";
import { ReceptionCenterUrlContext } from "./ReceptionCenterUrlContext";
import usePermissions from "@/utils/hooks/usePermissions";
import { permissions } from "@/utils/constants";
import { useSystem } from "@/utils/hooks/useLocation";

/**
 * /makkah/MED/center-reception: Makkah Nusuk list perms 178–181
 * /app/madina/center-reception: Madinah (system 2) — manifests 197,202; tafweejs 208,212
 */
const CenterReceptionMadina = () => {
  const { t } = useTranslation();
  const { port } = useParams();
  const { isMakkah } = useSystem();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    hasAnyPermission
  } = usePermissions();

  const isMakkahMed = isMakkah && port === "MED";
  const pageTitleKey = isMakkahMed
    ? "MAKKAH_NUSUK_CENTER_RECEPTION"
    : "MADINA_NUSUK_CENTER_RECEPTION";

  const canNusukPorts = isMakkahMed
    ? hasAnyPermission([
        permissions.list_makkah_reception_manifests,
        permissions.list_makkah_reception_manifest_pilgrims,
      ])
    : hasAnyPermission([
        permissions.list_madina_reception_manifests,
        permissions.list_madina_reception_manifest_pilgrims,
      ]);
  const canNusukBetween = isMakkahMed
    ? hasAnyPermission([
        permissions.list_makkah_reception_tafweejs,
        permissions.list_makkah_reception_tafweej_pilgrims,
      ])
    : hasAnyPermission([
        permissions.list_madina_reception_tafweejs,
        permissions.list_madina_reception_tafweej_pilgrims,
      ]);

  const defaultNusukSource = canNusukPorts ? "from_ports" : "between_cities";
  const nusukSourceParam = searchParams.get("source");

  const tabItems = useMemo(
    () =>
      [
        canNusukPorts && {
          key: "from_ports",
          label: t("FROM_PORTS"),
          icon: <HugeiconsIcon size={14} icon={Mosque02Icon} />,
        },
        canNusukBetween && {
          key: "between_cities",
          label: t("BETWEEN_CITIES"),
          icon: <HugeiconsIcon size={14} icon={City03Icon} />,
        },
      ].filter(Boolean),
    [canNusukPorts, canNusukBetween, t]
  );

  const resolvedSource = useMemo(() => {
    if (!tabItems.length) {
      return defaultNusukSource;
    }
    if (!nusukSourceParam) {
      return defaultNusukSource;
    }
    if (tabItems.some((tab) => tab.key === nusukSourceParam)) {
      return nusukSourceParam;
    }
    return tabItems[0].key;
  }, [nusukSourceParam, defaultNusukSource, tabItems]);

  const handleTabChange = (key) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("source", key);
        if (!next.get("date")) next.set("date", "today");
        return next;
      },
      { replace: true }
    );
  };

  const listUrlContextValue = useMemo(
    () => ({
      defaultSource: defaultNusukSource,
      activeSource: resolvedSource,
    }),
    [defaultNusukSource, resolvedSource]
  );

  const dateParam = searchParams.get("date") || "today";

  /** Keep URL in sync before paint; avoids first fetch using a different `source` than the active tab. */
  useLayoutEffect(() => {
    if (!tabItems.length) return;
    if (!nusukSourceParam) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("source", defaultNusukSource);
          if (!next.get("date")) next.set("date", "today");
          return next;
        },
        { replace: true }
      );
      return;
    }
    if (!tabItems.some((tab) => tab.key === nusukSourceParam)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("source", tabItems[0].key);
          if (!next.get("date")) next.set("date", "today");
          return next;
        },
        { replace: true }
      );
    }
  }, [nusukSourceParam, defaultNusukSource, setSearchParams, tabItems]);

  return (
    <ReceptionCenterUrlContext.Provider value={listUrlContextValue}>
      <Stack gap={3}>
        <PageHeader title={t(pageTitleKey)} sticky={false} />
        <Container fluid>
          {tabItems.length ? (
            <>
              <Tabs
                activeKey={resolvedSource}
                items={tabItems}
                onChange={handleTabChange}
                className="mb-3"
              />
              <FormPortsTab
                key={`${resolvedSource}-${dateParam}`}
              />
            </>
          ) : (
            <div className="text-muted">{t("NO_PERMISSIONS_AVAILABLE")}</div>
          )}
        </Container>
      </Stack>
    </ReceptionCenterUrlContext.Provider>
  );
};

export default CenterReceptionMadina;
